import { useI18n } from "@/i18n/I18nProvider";
import { Reveal } from "@/hooks/useReveal";
import { SEO } from "@/components/SEO";
import { Cpu, Network, Shield, Zap, Cloud } from "lucide-react";

const pillars = [
  { key: "ai", Icon: Cpu },
  { key: "scale", Icon: Network },
  { key: "sec", Icon: Shield },
  { key: "rt", Icon: Zap },
  { key: "cloud", Icon: Cloud },
] as const;

export default function Technology() {
  const { t } = useI18n();
  return (
    <>
      <SEO title={t.nav.technology} description={t.tech.lead} />

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-24 lg:pt-32 pb-20">
        <Reveal>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-6">— {t.tech.eyebrow}</p>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="font-display text-5xl lg:text-8xl font-semibold tracking-tighter leading-[0.95] max-w-5xl text-balance">
            {t.tech.title}
          </h1>
        </Reveal>
        <Reveal delay={240}>
          <p className="mt-8 max-w-3xl text-lg lg:text-xl text-muted-foreground leading-relaxed">
            {t.tech.lead}
          </p>
        </Reveal>
      </section>

      <section className="border-t hairline">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          {pillars.map(({ key, Icon }, i) => {
            const p = (t.tech.pillars as Record<string, { t: string; d: string }>)[key];
            return (
              <Reveal key={key}>
                <div className="grid lg:grid-cols-12 gap-10 py-16 lg:py-24 border-b hairline last:border-b-0 group">
                  <div className="lg:col-span-2">
                    <span className="font-mono text-xs text-muted-foreground">— 0{i + 1}</span>
                  </div>
                  <div className="lg:col-span-1">
                    <Icon className="h-7 w-7 text-foreground/80 transition-transform duration-500 group-hover:scale-110" strokeWidth={1.4} />
                  </div>
                  <div className="lg:col-span-9">
                    <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tighter mb-5">
                      {p.t}
                    </h2>
                    <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-3xl">
                      {p.d}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="border-t hairline bg-foreground/[0.015]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-40">
          <div className="grid lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-5">
              <h2 className="font-display text-4xl lg:text-6xl font-semibold tracking-tighter">
                {t.tech.whyTitle}
              </h2>
            </Reveal>
            <Reveal delay={120} className="lg:col-span-7 lg:pt-4">
              <p className="text-xl lg:text-2xl text-muted-foreground leading-relaxed text-pretty">
                {t.tech.whyBody}
              </p>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}